// StatisticsTab.js
import React, { useState, useEffect } from 'react';
import { authFetch } from '../authFetch';
import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Paper from '@mui/material/Paper';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';
import Stack from '@mui/material/Stack';

// Recharts
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid,
    Tooltip as RechartsTooltip, ResponsiveContainer,
    PieChart, Pie, Cell, LabelList,
} from 'recharts';

// MUI Icons
import RouteIcon from '@mui/icons-material/Route';
import HubIcon from '@mui/icons-material/Hub';
import PeopleIcon from '@mui/icons-material/People';
import StraightenIcon from '@mui/icons-material/Straighten';
import BridgeIcon from '@mui/icons-material/Architecture';
import TunnelIcon from '@mui/icons-material/Layers';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

// ─── Constants ────────────────────────────────────────────────────────────────

// Adjust this to match your actual network ID
const NETWORK_ID = 1;

const HIGHWAY_COLORS = {
    motorway:       '#e53935',
    trunk:          '#e64a19',
    primary:        '#f57c00',
    secondary:      '#f9a825',
    tertiary:       '#558b2f',
    residential:    '#1976d2',
    unclassified:   '#7b1fa2',
    service:        '#00838f',
    living_street:  '#4caf50',
    cycleway:       '#00acc1',
    path:           '#8d6e63',
    footway:        '#9e9e9e',
    unknown:        '#bdbdbd',
};

const PIE_COLORS = [
    '#1976d2', '#388e3c', '#f57c00', '#d32f2f',
    '#7b1fa2', '#0097a7', '#5d4037', '#455a64',
    '#e91e63', '#ff6f00', '#33691e', '#1a237e',
];

const DEMAND_COLORS = {
    Water:     '#0288d1',
    Power:     '#f57f17',
    Transport: '#2e7d32',
};

// Standardized height for all main bar charts
const CHART_HEIGHT = 320; 

// ─── Helpers ──────────────────────────────────────────────────────────────────

const formatNumber = (n) => {
    if (n === undefined || n === null) return '—';
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
    if (n >= 1_000)     return `${(n / 1_000).toFixed(1)}K`;
    return n.toLocaleString(undefined, { maximumFractionDigits: 1 });
};

const toPercent = (part, total) =>
    total ? `${((part / total) * 100).toFixed(1)}%` : '—';

// ─── KPI Card ─────────────────────────────────────────────────────────────────

function KpiCard({ label, value, subtitle, icon: Icon, color, tooltip }) {
    const theme = useTheme();
    return (
        <Card
            elevation={0}
            sx={{
                border: `1px solid ${theme.palette.divider}`,
                borderTop: `3px solid ${color}`,
                height: '100%',
            }}
        >
            <CardContent sx={{ p: 2.5 }}>
                <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
                    <Box>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                            {label}
                            {tooltip && (
                                <Tooltip title={tooltip} placement="top" arrow>
                                    <InfoOutlinedIcon
                                        sx={{ fontSize: 13, ml: 0.5, verticalAlign: 'middle', opacity: 0.5 }}
                                    />
                                </Tooltip>
                            )}
                        </Typography>
                        <Typography variant="h4" fontWeight={700} color="text.primary">
                            {value}
                        </Typography>
                        {subtitle && (
                            <Typography variant="caption" color="text.secondary">
                                {subtitle}
                            </Typography>
                        )}
                    </Box>
                    <Box
                        sx={{
                            width: 40,
                            height: 40,
                            borderRadius: 2,
                            bgcolor: `${color}18`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                        }}
                    >
                        <Icon sx={{ color, fontSize: 22 }} />
                    </Box>
                </Stack>
            </CardContent>
        </Card>
    );
}

// ─── Custom recharts tooltip ──────────────────────────────────────────────────

function CustomTooltip({ active, payload, label, unit = '' }) {
    const theme = useTheme();
    if (!active || !payload?.length) return null;
    return (
        <Paper
            elevation={4}
            sx={{
                px: 1.5, py: 1,
                border: `1px solid ${theme.palette.divider}`,
                fontSize: 13,
            }}
        >
            <Typography variant="caption" fontWeight={600}>{label}</Typography>
            {payload.map((p) => (
                <Box key={p.name} sx={{ color: p.color, mt: 0.3 }}>
                    {p.name}: <strong>{formatNumber(p.value)}{unit}</strong>
                </Box>
            ))}
        </Paper>
    );
}

// ─── Highway colour helper for charts ────────────────────────────────────────

const hwColor = (name) =>
    HIGHWAY_COLORS[name] || HIGHWAY_COLORS.unknown;

// ─── Tab Panel ────────────────────────────────────────────────────────────────

function TabPanel({ children, value, index }) {
    return (
        <Box hidden={value !== index} sx={{ pt: 3 }}>
            {value === index && children}
        </Box>
    );
}

// ─── Road Network Tab ─────────────────────────────────────────────────────────

function RoadNetworkTab({ edgeStats }) {
    const theme = useTheme();
    const { 
        highway_type_distribution = [],
        highway_type_lengths_km = [],
        maxspeed_distribution = [],
        lane_distribution = [],
        bridge_count = 0,
        tunnel_count = 0,
        oneway_count = 0,
        twoway_count = 0,
        total_edges = 0,
    } = edgeStats;

    // Filter to top 10 to prevent crowding
    const topHwBarData = highway_type_distribution.slice(0, 10).map((d) => ({
        ...d,
        fill: hwColor(d.name),
    }));
    
    // Filter to top 10 lengths
    const topHwLengths = highway_type_lengths_km.slice(0, 10);

    const onewayPie = [
        { name: 'One-way', value: oneway_count },
        { name: 'Two-way', value: twoway_count },
    ];

    return (
        <Grid container spacing={3} alignItems="stretch">

            {/* ── Highway type distribution ── */}
            <Grid item xs={12} md={6}>
                <Card elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, height: '100%' }}>
                    <CardHeader
                        title="Highway Type Distribution"
                        titleTypographyProps={{ variant: 'subtitle1', fontWeight: 600 }}
                        subheader="Top 10 road classes by segment count"
                        subheaderTypographyProps={{ variant: 'caption' }}
                    />
                    <CardContent>
                        <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
                            <BarChart
                                data={topHwBarData}
                                layout="vertical"
                                margin={{ left: 10, right: 30, top: 0, bottom: 0 }}
                            >
                                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                                <XAxis type="number" tick={{ fontSize: 11 }} />
                                <YAxis dataKey="name" type="category" tick={{ fontSize: 11 }} width={95} />
                                <RechartsTooltip content={<CustomTooltip unit=" segments" />} />
                                <Bar dataKey="count" radius={[0, 3, 3, 0]} maxBarSize={22}>
                                    {topHwBarData.map((entry, i) => (
                                        <Cell key={i} fill={entry.fill} />
                                    ))}
                                    <LabelList dataKey="count" position="right" style={{ fontSize: 11 }} />
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </CardContent>
                </Card>
            </Grid>

            {/* ── Network length by road type ── */}
            <Grid item xs={12} md={6}>
                <Card elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, height: '100%' }}>
                    <CardHeader
                        title="Network Length by Road Class"
                        titleTypographyProps={{ variant: 'subtitle1', fontWeight: 600 }}
                        subheader="Top 10 road classes by total kilometres"
                        subheaderTypographyProps={{ variant: 'caption' }}
                    />
                    <CardContent>
                        <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
                            <BarChart
                                data={topHwLengths}
                                layout="vertical"
                                margin={{ left: 10, right: 50, top: 0, bottom: 0 }}
                            >
                                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                                <XAxis type="number" tick={{ fontSize: 11 }} unit=" km" />
                                <YAxis dataKey="name" type="category" tick={{ fontSize: 11 }} width={95} />
                                <RechartsTooltip content={<CustomTooltip unit=" km" />} />
                                <Bar dataKey="length_km" name="Length" radius={[0, 3, 3, 0]} maxBarSize={22}>
                                    {topHwLengths.map((entry, i) => (
                                        <Cell key={i} fill={hwColor(entry.name)} />
                                    ))}
                                    <LabelList
                                        dataKey="length_km"
                                        position="right"
                                        formatter={(v) => `${v} km`}
                                        style={{ fontSize: 11 }}
                                    />
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </CardContent>
                </Card>
            </Grid>

            {/* ── Max speed distribution ── */}
            <Grid item xs={12} md={6}>
                <Card elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, height: '100%' }}>
                    <CardHeader
                        title="Speed Limit Distribution"
                        titleTypographyProps={{ variant: 'subtitle1', fontWeight: 600 }}
                        subheader="Number of segments per posted speed limit (km/h)"
                        subheaderTypographyProps={{ variant: 'caption' }}
                    />
                    <CardContent>
                        <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
                            <BarChart
                                data={maxspeed_distribution}
                                margin={{ left: 0, right: 10, top: 5, bottom: 30 }}
                            >
                                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                <XAxis
                                    dataKey="speed"
                                    tick={{ fontSize: 11 }}
                                    angle={-35}
                                    textAnchor="end"
                                    interval={0}
                                    label={{ value: 'Speed (km/h)', position: 'insideBottom', offset: -18, fontSize: 11 }}
                                />
                                <YAxis tick={{ fontSize: 11 }} />
                                <RechartsTooltip content={<CustomTooltip unit=" segments" />} />
                                <Bar
                                    dataKey="count"
                                    name="Segments"
                                    fill={theme.palette.warning.main}
                                    radius={[3, 3, 0, 0]}
                                    maxBarSize={40}
                                />
                            </BarChart>
                        </ResponsiveContainer>
                    </CardContent>
                </Card>
            </Grid>

            {/* ── Lane distribution ── */}
            <Grid item xs={12} md={6}>
                <Card elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, height: '100%' }}>
                    <CardHeader
                        title="Lane Count Distribution"
                        titleTypographyProps={{ variant: 'subtitle1', fontWeight: 600 }}
                        subheader="Segments grouped by number of lanes"
                        subheaderTypographyProps={{ variant: 'caption' }}
                    />
                    <CardContent>
                        <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
                            <BarChart
                                data={lane_distribution}
                                margin={{ left: 0, right: 20, top: 5, bottom: 5 }}
                            >
                                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                <XAxis dataKey="lanes" tick={{ fontSize: 11 }} />
                                <YAxis tick={{ fontSize: 11 }} />
                                <RechartsTooltip content={<CustomTooltip unit=" segments" />} />
                                <Bar
                                    dataKey="count"
                                    name="Segments"
                                    fill={theme.palette.success.main}
                                    radius={[3, 3, 0, 0]}
                                    maxBarSize={50}
                                >
                                    <LabelList dataKey="count" position="top" style={{ fontSize: 11 }} />
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </CardContent>
                </Card>
            </Grid>

            {/* ── One-way vs Two-way + road properties ── */}
            <Grid item xs={12} md={6}>
                <Card elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <CardHeader
                        title="Direction & Special Features"
                        titleTypographyProps={{ variant: 'subtitle1', fontWeight: 600 }}
                    />
                    <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                        <Box sx={{ display: 'flex', gap: 4, alignItems: 'center', justifyContent: 'center', mb: 3 }}>
                            <Box sx={{ width: 160, height: 160 }}>
                                <ResponsiveContainer width="100%" height="100%">
                                    <PieChart>
                                        <Pie
                                            data={onewayPie}
                                            cx="50%"
                                            cy="50%"
                                            innerRadius={50}
                                            outerRadius={75}
                                            dataKey="value"
                                            paddingAngle={3}
                                        >
                                            <Cell fill={theme.palette.primary.main} />
                                            <Cell fill={theme.palette.grey[300]} />
                                        </Pie>
                                        <RechartsTooltip formatter={(v) => formatNumber(v)} />
                                    </PieChart>
                                </ResponsiveContainer>
                            </Box>
                            <Box>
                                {onewayPie.map((d, i) => (
                                    <Box key={d.name} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                                        <Box
                                            sx={{
                                                width: 12, height: 12, borderRadius: '50%',
                                                bgcolor: i === 0 ? theme.palette.primary.main : theme.palette.grey[300],
                                            }}
                                        />
                                        <Typography variant="body1">
                                            {d.name}: <strong>{formatNumber(d.value)}</strong>
                                            <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                                ({toPercent(d.value, total_edges)})
                                            </Typography>
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        </Box>

                        <Divider sx={{ my: 2 }} />

                        <Stack direction="row" spacing={2} justifyContent="space-around">
                            {[
                                { label: 'Bridges', value: bridge_count, icon: BridgeIcon, color: theme.palette.warning.main },
                                { label: 'Tunnels', value: tunnel_count, icon: TunnelIcon, color: theme.palette.info.main },
                            ].map(({ label, value, icon: Ic, color }) => (
                                <Box key={label} textAlign="center">
                                    <Box
                                        sx={{
                                            width: 48, height: 48, borderRadius: 2,
                                            bgcolor: `${color}18`,
                                            display: 'flex', alignItems: 'center',
                                            justifyContent: 'center', mx: 'auto', mb: 1,
                                        }}
                                    >
                                        <Ic sx={{ color, fontSize: 24 }} />
                                    </Box>
                                    <Typography variant="h5" fontWeight={700}>{formatNumber(value)}</Typography>
                                    <Typography variant="body2" color="text.secondary">{label}</Typography>
                                </Box>
                            ))}
                        </Stack>
                    </CardContent>
                </Card>
            </Grid>
        </Grid>
    );
}

// ─── Infrastructure Nodes Tab ─────────────────────────────────────────────────

function InfrastructureNodesTab({ nodeStats }) {
    const theme = useTheme();
    const {
        ci_type_distribution = [],
        sector_distribution = [],
        population_buckets = [],
        demand_comparison = [],
    } = nodeStats;

    // Filter out "Road" and "Transport" nodes so they don't skew the charts visually
    const filteredCiTypes = ci_type_distribution.filter(d => d.name !== 'Road');
    const filteredCiTotal = filteredCiTypes.reduce((sum, d) => sum + d.count, 0);
    
    const filteredSectors = sector_distribution.filter(d => d.name !== 'Transport' && d.name !== 'Road');

    return (
        <Grid container spacing={3} alignItems="stretch">

            {/* ── CI Type distribution (pie) ── */}
            <Grid item xs={12} md={6}>
                <Card elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, height: '100%' }}>
                    <CardHeader
                        title="CI Type Breakdown"
                        titleTypographyProps={{ variant: 'subtitle1', fontWeight: 600 }}
                        subheader="Excluding standard road segments"
                        subheaderTypographyProps={{ variant: 'caption' }}
                    />
                    {/* flexGrow: 1 allows card to expand without triggering internal scrollbars */}
                    <CardContent sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                        {/* Fixed height for the pie chart so chips fill the rest of the available space naturally */}
                        <ResponsiveContainer width="100%" height={240}>
                            <PieChart>
                                <Pie
                                    data={filteredCiTypes}
                                    cx="50%"
                                    cy="50%"
                                    outerRadius={100}
                                    innerRadius={50}
                                    dataKey="count"
                                    nameKey="name"
                                    paddingAngle={2}
                                >
                                    {filteredCiTypes.map((_, i) => (
                                        <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                                    ))}
                                </Pie>
                                <RechartsTooltip formatter={(v) => formatNumber(v)} />
                            </PieChart>
                        </ResponsiveContainer>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center', mt: 2 }}>
                            {filteredCiTypes.map((d, i) => (
                                <Chip
                                    key={d.name}
                                    size="small"
                                    label={`${d.name}: ${formatNumber(d.count)} (${toPercent(d.count, filteredCiTotal)})`}
                                    sx={{
                                        bgcolor: `${PIE_COLORS[i % PIE_COLORS.length]}18`,
                                        color: PIE_COLORS[i % PIE_COLORS.length],
                                        border: `1px solid ${PIE_COLORS[i % PIE_COLORS.length]}`,
                                        fontWeight: 600,
                                        fontSize: 11,
                                    }}
                                />
                            ))}
                        </Box>
                    </CardContent>
                </Card>
            </Grid>

            {/* ── Sector distribution (bar) ── */}
            <Grid item xs={12} md={6}>
                <Card elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, height: '100%' }}>
                    <CardHeader
                        title="Sector Distribution"
                        titleTypographyProps={{ variant: 'subtitle1', fontWeight: 600 }}
                        subheader="Node count by infrastructure sector (excluding Transport)"
                        subheaderTypographyProps={{ variant: 'caption' }}
                    />
                    <CardContent>
                        <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
                            <BarChart
                                data={filteredSectors}
                                layout="vertical"
                                margin={{ left: 10, right: 50, top: 0, bottom: 0 }}
                            >
                                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                                <XAxis type="number" tick={{ fontSize: 11 }} />
                                <YAxis dataKey="name" type="category" tick={{ fontSize: 11 }} width={90} />
                                <RechartsTooltip content={<CustomTooltip unit=" nodes" />} />
                                <Bar dataKey="count" name="Nodes" radius={[0, 3, 3, 0]} maxBarSize={28}>
                                    {filteredSectors.map((_, i) => (
                                        <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                                    ))}
                                    <LabelList dataKey="count" position="right" style={{ fontSize: 11 }} />
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </CardContent>
                </Card>
            </Grid>

            {/* ── Population distribution ── */}
            <Grid item xs={12} md={6}>
                <Card elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, height: '100%' }}>
                    <CardHeader
                        title="Population Distribution"
                        titleTypographyProps={{ variant: 'subtitle1', fontWeight: 600 }}
                        subheader="Number of nodes grouped by assigned population size"
                        subheaderTypographyProps={{ variant: 'caption' }}
                    />
                    <CardContent>
                        <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
                            <BarChart
                                data={population_buckets}
                                margin={{ left: 0, right: 20, top: 5, bottom: 5 }}
                            >
                                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                <XAxis dataKey="range" tick={{ fontSize: 11 }} />
                                <YAxis tick={{ fontSize: 11 }} />
                                <RechartsTooltip content={<CustomTooltip unit=" nodes" />} />
                                <Bar
                                    dataKey="count"
                                    name="Nodes"
                                    fill={theme.palette.primary.main}
                                    radius={[3, 3, 0, 0]}
                                    maxBarSize={50}
                                >
                                    <LabelList dataKey="count" position="top" style={{ fontSize: 11 }} />
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </CardContent>
                </Card>
            </Grid>

            {/* ── Demand comparison ── */}
            <Grid item xs={12} md={6}>
                <Card elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <CardHeader
                        title="System Demand Summary"
                        titleTypographyProps={{ variant: 'subtitle1', fontWeight: 600 }}
                        subheader="Average, total, and peak demand per infrastructure system (in per capita demand units)"
                        subheaderTypographyProps={{ variant: 'caption' }}
                    />
                    {/* flexGrow: 1 instead of fixed CHART_HEIGHT to prevent overlapping text */}
                    <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                        <Stack spacing={2}>
                            {demand_comparison.map((d) => {
                                const color = DEMAND_COLORS[d.system] || theme.palette.primary.main;
                                return (
                                    <Box
                                        key={d.system}
                                        sx={{
                                            p: 1.5,
                                            borderRadius: 2,
                                            border: `1px solid ${color}30`,
                                            bgcolor: `${color}08`,
                                        }}
                                    >
                                        <Typography
                                            variant="subtitle1"
                                            fontWeight={700}
                                            sx={{ color, mb: 1 }}
                                        >
                                            {d.system} Demand
                                        </Typography>
                                        <Stack direction="row" spacing={4}>
                                            {[
                                                { label: 'Avg / node', value: d.avg },
                                                { label: 'Total', value: formatNumber(d.total) },
                                                { label: 'Peak', value: formatNumber(d.max) },
                                            ].map(({ label, value }) => (
                                                <Box key={label}>
                                                    <Typography variant="body2" color="text.secondary">{label}</Typography>
                                                    <Typography variant="body1" fontWeight={600}>{value}</Typography>
                                                </Box>
                                            ))}
                                        </Stack>
                                    </Box>
                                );
                            })}
                        </Stack>
                    </CardContent>
                </Card>
            </Grid>

        </Grid>
    );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function StatisticsTab() {
    const theme = useTheme();
    const [tabValue, setTabValue] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [data, setData] = useState(null);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);

        authFetch(`/api/networks/${NETWORK_ID}/statistics/`)
            .then((res) => res.json())
            .then((json) => {
                if (!cancelled) {
                    setData(json);
                    setLoading(false);
                }
            })
            .catch((err) => {
                if (!cancelled) {
                    setError(err.message);
                    setLoading(false);
                }
            });

        return () => { cancelled = true; };
    }, []);

    // ── Loading ──────────────────────────────────────────────────────────────
    if (loading) {
        return (
            <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="50vh" gap={2}>
                <CircularProgress size={36} />
                <Typography variant="body2" color="text.secondary">
                    Loading network statistics… (first load may take a few seconds)
                </Typography>
            </Box>
        );
    }

    // ── Error ────────────────────────────────────────────────────────────────
    if (error) {
        return (
            <Container maxWidth="md" sx={{ mt: 4 }}>
                <Alert severity="error" variant="outlined">
                    <strong>Could not load statistics:</strong> {error}
                </Alert>
            </Container>
        );
    }

    const { edge_statistics: es, node_statistics: ns } = data;

    // ── KPI values ───────────────────────────────────────────────────────────
    const kpis = [
        {
            label: 'Road Segments',
            value: formatNumber(es.total_edges),
            subtitle: 'Network edges',
            icon: RouteIcon,
            color: theme.palette.primary.main,
        },
        {
            label: 'Total Network Length',
            value: `${formatNumber(es.total_length_km)} km`,
            subtitle: 'Sum of all segment lengths',
            icon: StraightenIcon,
            color: theme.palette.warning.main,
        },
        {
            label: 'Infrastructure Nodes',
            value: formatNumber(ns.total_nodes),
            subtitle: `${ns.ci_type_distribution.length} CI types`,
            icon: HubIcon,
            color: theme.palette.success.main,
        },
        {
            label: 'Total Population Served',
            value: formatNumber(ns.total_population),
            subtitle: `Avg ${formatNumber(ns.avg_population)} per node · Peak ${formatNumber(ns.max_population)}`,
            icon: PeopleIcon,
            color: theme.palette.secondary.main,
            tooltip: 'Sum of population demand values across all infrastructure nodes',
        },
    ];

    return (
        <Container maxWidth="xl">
            <Box sx={{ width: '100%' }}>

                {/* ── Page title ── */}
                <Typography component="h1" variant="h5" fontWeight={700} sx={{ my: 3 }}>
                    Infrastructure Network Statistics
                </Typography>

                {/* ── KPI Cards ── */}
                <Grid container spacing={2} sx={{ mb: 4 }}>
                    {kpis.map((kpi) => (
                        <Grid item xs={12} sm={6} md={3} key={kpi.label}>
                            <KpiCard {...kpi} />
                        </Grid>
                    ))}
                </Grid>

                {/* ── Main Tabs ── */}
                <Paper elevation={0} sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Tabs
                        value={tabValue}
                        onChange={(_, v) => setTabValue(v)}
                        aria-label="network statistics tabs"
                    >
                        <Tab label="Road Network" id="stats-tab-0" />
                        <Tab label="Infrastructure Nodes" id="stats-tab-1" />
                    </Tabs>
                </Paper>

                <TabPanel value={tabValue} index={0}>
                    <RoadNetworkTab edgeStats={es} />
                </TabPanel>

                <TabPanel value={tabValue} index={1}>
                    <InfrastructureNodesTab nodeStats={ns} />
                </TabPanel>

            </Box>
        </Container>
    );
}